import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFieldArray, useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import { Bouton, Champ, Etiquette, Liste, Pastille, Saisie } from '../components/ui/Base'
import { Bandeau, EtatVide, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import { ApiError } from '../lib/api/erreurs'
import type { Cabinet, LigneOrdonnance, Ordonnance, Page, Patient, TypeSoin, Utilisateur } from '../lib/api/types'
import { decrireLigneOrdonnance } from '../lib/formatters/recurrence'
import { aujourdhui, JOURS_SEMAINE_COURT } from '../lib/temps'

/**
 * Saisie d'une ordonnance et de ses lignes (§5.7 et §5.8).
 *
 * ============================================================================
 * C'EST ICI QUE NAÎT LE PLANNING.
 *
 * Le §2 est explicite : « la récurrence est le cœur du produit ». Une
 * intervention ne se saisit pas une par une — on décrit une **prescription**
 * (quel soin, à quel rythme, entre quelles dates), et le moteur en déduit
 * chaque passage. Une ordonnance de trois mois à deux passages quotidiens,
 * c'est cent quatre-vingts interventions engendrées par cet écran.
 *
 * D'où le parti pris de l'interface : chaque ligne affiche en permanence la
 * **phrase** qu'elle produit — « Injection, tous les jours à 08:00 et 19:00, du
 * 01/03 au 31/05 ». On relit une phrase ; on ne relit pas un formulaire.
 * ============================================================================
 */

const HEURE = /^([01]\d|2[0-3]):[0-5]\d$/

const ligneSchema = z
  .object({
    type_soin_id: z.string().min(1, 'Choisissez un type de soin.'),
    frequence: z.enum(['QUOTIDIEN', 'JOURS_SEMAINE', 'INTERVALLE_JOURS']),
    /** Cases à cocher, **0 = lundi**. Jamais `getDay()` (§5.8). */
    jours: z.array(z.number().int().min(0).max(6)),
    intervalle_jours: z.coerce.number().int().min(1).max(365).optional(),
    /** Saisi en clair : « 08:00, 19:00 ». Découpé et vérifié ci-dessous. */
    horaires: z.string().min(1, 'Indiquez au moins un horaire de passage.'),
    duree_minutes_override: z.string().optional(),
    infirmier_prefere_id: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    const horaires = decouperHoraires(v.horaires)
    if (horaires.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['horaires'],
        message: 'Format attendu : HH:MM, séparés par des virgules.',
      })
    }
    for (const h of horaires) {
      if (!HEURE.test(h)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['horaires'],
          message: `« ${h} » n'est pas une heure valide (HH:MM).`,
        })
        break
      }
    }
    // Une fréquence « certains jours » sans aucun jour coché ne produirait
    // aucune intervention — un silence, là où l'utilisateur attend un planning.
    if (v.frequence === 'JOURS_SEMAINE' && v.jours.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['jours'],
        message: 'Cochez au moins un jour de la semaine.',
      })
    }
    if (v.frequence === 'INTERVALLE_JOURS' && !v.intervalle_jours) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['intervalle_jours'],
        message: "Précisez l'intervalle, en jours.",
      })
    }
  })

const schema = z
  .object({
    medecin_prescripteur: z.string().min(1, 'Le médecin prescripteur est obligatoire.'),
    medecin_rpps: z.string().regex(/^\d{11}$/, 'Le RPPS comporte 11 chiffres.').or(z.literal('')),
    date_prescription: z.string().min(1, 'La date de prescription est obligatoire.'),
    date_debut: z.string().min(1, 'La date de début est obligatoire.'),
    date_fin: z.string().min(1, 'La date de fin est obligatoire.'),
    notes: z.string().optional(),
    lignes: z.array(ligneSchema).min(1, 'Une ordonnance comporte au moins un soin.'),
  })
  .superRefine((v, ctx) => {
    // Comparaison de chaînes « AAAA-MM-JJ » : lexicographique et
    // chronologique à la fois, sans jamais construire de `Date` (§4).
    if (v.date_fin < v.date_debut) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_fin'],
        message: 'La date de fin doit suivre la date de début.',
      })
    }
    if (v.date_debut < v.date_prescription) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_debut'],
        message: 'Les soins ne peuvent pas commencer avant la prescription.',
      })
    }
  })

type Formulaire = z.infer<typeof schema>

/** « 08:00, 19h00 ; 12:30 » → ["08:00","19h00","12:30"]. Le tri et l'unicité viennent du serveur. */
function decouperHoraires(brut: string): string[] {
  return brut
    .split(/[,;]/)
    .map((h) => h.trim())
    .filter(Boolean)
}

const LIGNE_VIDE = {
  type_soin_id: '',
  frequence: 'QUOTIDIEN' as const,
  jours: [] as number[],
  intervalle_jours: undefined,
  horaires: '08:00',
  duree_minutes_override: '',
  infirmier_prefere_id: '',
}

export function OrdonnanceFormulaire() {
  const { id = '' } = useParams()
  const naviguer = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()

  const requetePatient = useQuery({
    queryKey: cles.patients.detail(id),
    queryFn: ({ signal }) => api.get<Patient>(`/patients/${id}`, signal),
    enabled: Boolean(id),
  })

  const requeteSoins = useQuery({
    queryKey: cles.typesSoins.liste(),
    queryFn: ({ signal }) => api.get<Page<TypeSoin>>('/types-soins', signal),
    staleTime: 5 * 60_000,
  })

  const requeteInfirmiers = useQuery({
    queryKey: cles.utilisateurs.liste(),
    queryFn: ({ signal }) => api.get<Page<Utilisateur>>('/utilisateurs', signal),
    // Un collaborateur n'a pas le droit de lister les infirmiers (R6) : le
    // champ « infirmier attitré » disparaît alors, sans erreur affichée.
    retry: false,
    staleTime: 5 * 60_000,
  })

  const requeteCabinet = useQuery({
    queryKey: cles.cabinet,
    queryFn: ({ signal }) => api.get<Cabinet>('/cabinet', signal),
    staleTime: 5 * 60_000,
  })

  const { register, control, handleSubmit, watch, setValue, formState, setError } =
    useForm<Formulaire>({
      resolver: zodResolver(schema),
      defaultValues: {
        medecin_prescripteur: '',
        medecin_rpps: '',
        date_prescription: aujourdhui(),
        date_debut: aujourdhui(),
        date_fin: '',
        notes: '',
        lignes: [LIGNE_VIDE],
      },
    })

  const { fields, append, remove } = useFieldArray({ control, name: 'lignes' })
  const valeurs = watch()

  const enregistrer = useMutation({
    mutationFn: async (v: Formulaire) => {
      const ordonnance = await api.post<Ordonnance>(`/patients/${id}/ordonnances`, {
        medecin_prescripteur: v.medecin_prescripteur.trim(),
        medecin_rpps: v.medecin_rpps.trim() || null,
        date_prescription: v.date_prescription,
        date_debut: v.date_debut,
        date_fin: v.date_fin,
        notes: v.notes?.trim() || null,
      })

      // ====================================================================
      // Les lignes partent **en série**, pas en parallèle.
      //
      // L'API ne connaît pas de création atomique ordonnance + lignes. En
      // parallèle, un refus sur la troisième ligne laisse les deux premières
      // enregistrées sans qu'on sache lesquelles : ici, la première qui
      // échoue arrête la suite, et le message nomme son rang.
      // ====================================================================
      for (const [rang, ligne] of v.lignes.entries()) {
        try {
          await api.post<LigneOrdonnance>(`/ordonnances/${ordonnance.id}/lignes`, {
            type_soin_id: ligne.type_soin_id,
            frequence: ligne.frequence,
            jours_semaine:
              ligne.frequence === 'JOURS_SEMAINE'
                ? [...ligne.jours].sort((a, b) => a - b).join(',')
                : null,
            intervalle_jours:
              ligne.frequence === 'INTERVALLE_JOURS' ? (ligne.intervalle_jours ?? null) : null,
            horaires: decouperHoraires(ligne.horaires),
            duree_minutes_override: ligne.duree_minutes_override
              ? Number(ligne.duree_minutes_override)
              : null,
            infirmier_prefere_id: ligne.infirmier_prefere_id || null,
          })
        } catch (cause) {
          const detail = cause instanceof Error ? cause.message : 'refus du serveur'
          throw new Error(`Soin n° ${rang + 1} refusé : ${detail}`)
        }
      }
      return ordonnance
    },
    onSuccess: () => {
      toast('Ordonnance enregistrée. Le planning peut être généré.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.patients.ordonnances(id) })
      void queryClient.invalidateQueries({ queryKey: cles.planning.tout })
      naviguer(`/patients/${id}`)
    },
    onError: (erreur) => {
      if (erreur instanceof ApiError && erreur.champ === 'date_fin') {
        setError('date_fin', { message: erreur.message })
        return
      }
      toast(erreur instanceof Error ? erreur.message : 'Enregistrement impossible.', 'erreur')
    },
  })

  if (requetePatient.isPending) return <Squelette lignes={10} />
  if (requetePatient.isError) {
    return <MessageErreur erreur={requetePatient.error} onReessayer={() => requetePatient.refetch()} />
  }

  const soins = requeteSoins.data?.items ?? []
  const infirmiers = requeteInfirmiers.data?.items ?? []
  const cabinet = requeteCabinet.data
  const e = formState.errors

  // Sans référentiel d'actes, aucune ligne n'est saisissable : on le dit
  // franchement plutôt que d'afficher une liste déroulante vide.
  if (!requeteSoins.isPending && soins.length === 0) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link to={`/patients/${id}`} className="text-sm text-sky-700 hover:underline">
          ← Retour à la fiche
        </Link>
        <EtatVide
          icone="💉"
          titre="Aucun type de soin n'est déclaré"
          description="Une ordonnance prescrit un soin. Déclarez d'abord le référentiel des actes du cabinet — cinq suffisent pour commencer."
          action={<Bouton onClick={() => naviguer('/types-soins')}>Ouvrir les types de soins</Bouton>}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <Link to={`/patients/${id}`} className="text-sm text-sky-700 hover:underline">
          ← {requetePatient.data.prenom} {requetePatient.data.nom}
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-slate-900">Nouvelle ordonnance</h1>
        <p className="text-sm text-slate-600">
          Décrivez la prescription et son rythme : les interventions en sont déduites.
        </p>
      </div>

      <form
        onSubmit={handleSubmit((v) => enregistrer.mutate(v))}
        className="space-y-6"
        noValidate
      >
        {/* ── Prescription ── */}
        <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Prescription
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ
              libelle="Médecin prescripteur"
              id="medecin"
              erreur={e.medecin_prescripteur?.message}
              obligatoire
            >
              <Saisie id="medecin" placeholder="Dr …" {...register('medecin_prescripteur')} />
            </Champ>
            <Champ libelle="RPPS du médecin" id="rpps" erreur={e.medecin_rpps?.message} aide="11 chiffres">
              <Saisie id="rpps" inputMode="numeric" {...register('medecin_rpps')} />
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ
              libelle="Date de prescription"
              id="prescription"
              erreur={e.date_prescription?.message}
              obligatoire
            >
              <Saisie id="prescription" type="date" {...register('date_prescription')} />
            </Champ>
            <Champ libelle="Début des soins" id="debut" erreur={e.date_debut?.message} obligatoire>
              <Saisie id="debut" type="date" {...register('date_debut')} />
            </Champ>
            <Champ
              libelle="Fin des soins"
              id="fin"
              erreur={e.date_fin?.message}
              aide="Borne de génération du planning."
              obligatoire
            >
              <Saisie id="fin" type="date" {...register('date_fin')} />
            </Champ>
          </div>
          <Champ libelle="Notes" id="notes">
            <Saisie id="notes" {...register('notes')} />
          </Champ>
        </section>

        {/* ── Soins prescrits ── */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Soins prescrits
            </h2>
            <Bouton variante="secondaire" type="button" onClick={() => append(LIGNE_VIDE)}>
              Ajouter un soin
            </Bouton>
          </div>

          {e.lignes?.message ? (
            <p className="text-sm font-medium text-red-700" role="alert">
              {e.lignes.message}
            </p>
          ) : null}

          {fields.map((champ, i) => {
            const ligne = valeurs.lignes?.[i]
            const soin = soins.find((s) => s.id === ligne?.type_soin_id)
            const erreursLigne = e.lignes?.[i]

            return (
              <fieldset key={champ.id} className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
                <legend className="flex w-full items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                    {soin ? <Pastille couleur={soin.couleur} /> : null}
                    Soin n° {i + 1}
                  </span>
                  {fields.length > 1 ? (
                    <Bouton variante="discret" type="button" onClick={() => remove(i)}>
                      Retirer
                    </Bouton>
                  ) : null}
                </legend>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Champ
                    libelle="Type de soin"
                    id={`soin-${i}`}
                    erreur={erreursLigne?.type_soin_id?.message}
                    obligatoire
                  >
                    <Liste id={`soin-${i}`} {...register(`lignes.${i}.type_soin_id`)}>
                      <option value="">Choisir…</option>
                      {soins
                        .filter((s) => s.actif)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.code} — {s.libelle} ({s.duree_minutes} min)
                          </option>
                        ))}
                    </Liste>
                  </Champ>

                  <Champ libelle="Rythme" id={`freq-${i}`}>
                    <Liste id={`freq-${i}`} {...register(`lignes.${i}.frequence`)}>
                      <option value="QUOTIDIEN">Tous les jours</option>
                      <option value="JOURS_SEMAINE">Certains jours de la semaine</option>
                      <option value="INTERVALLE_JOURS">Tous les N jours</option>
                    </Liste>
                  </Champ>
                </div>

                {/* Cases à cocher des jours — **0 = lundi**. La grille commence
                    au lundi partout dans le produit ; changer d'origine ici
                    décalerait toute la génération d'un jour. */}
                {ligne?.frequence === 'JOURS_SEMAINE' ? (
                  <Champ libelle="Jours" id={`jours-${i}`} erreur={erreursLigne?.jours?.message}>
                    <div className="flex flex-wrap gap-2">
                      {JOURS_SEMAINE_COURT.map((libelle, index) => {
                        const coche = ligne.jours.includes(index)
                        return (
                          <button
                            key={libelle}
                            type="button"
                            aria-pressed={coche}
                            onClick={() =>
                              setValue(
                                `lignes.${i}.jours`,
                                coche
                                  ? ligne.jours.filter((j) => j !== index)
                                  : [...ligne.jours, index],
                                { shouldValidate: true },
                              )
                            }
                            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition ${
                              coche
                                ? 'border-sky-600 bg-sky-50 text-sky-800'
                                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {libelle}
                          </button>
                        )
                      })}
                    </div>
                  </Champ>
                ) : null}

                {ligne?.frequence === 'INTERVALLE_JOURS' ? (
                  <Champ
                    libelle="Un passage tous les … jours"
                    id={`intervalle-${i}`}
                    erreur={erreursLigne?.intervalle_jours?.message}
                    aide="2 = un jour sur deux."
                  >
                    <Saisie
                      id={`intervalle-${i}`}
                      type="number"
                      min={1}
                      max={365}
                      className="max-w-32"
                      {...register(`lignes.${i}.intervalle_jours`)}
                    />
                  </Champ>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-3">
                  <Champ
                    libelle="Horaires de passage"
                    id={`horaires-${i}`}
                    erreur={erreursLigne?.horaires?.message}
                    aide={
                      cabinet
                        ? `HH:MM séparés par des virgules, entre ${cabinet.heure_ouverture.slice(0, 5)} et ${cabinet.heure_fermeture.slice(0, 5)}.`
                        : 'HH:MM séparés par des virgules. Deux horaires = deux passages par jour.'
                    }
                    obligatoire
                  >
                    <Saisie
                      id={`horaires-${i}`}
                      placeholder="08:00, 19:00"
                      {...register(`lignes.${i}.horaires`)}
                    />
                  </Champ>

                  <Champ
                    libelle="Durée particulière (min)"
                    id={`duree-${i}`}
                    aide={soin ? `Par défaut : ${soin.duree_minutes} min.` : 'Laisser vide pour la durée standard.'}
                  >
                    <Saisie
                      id={`duree-${i}`}
                      type="number"
                      min={5}
                      step={5}
                      {...register(`lignes.${i}.duree_minutes_override`)}
                    />
                  </Champ>

                  {infirmiers.length > 0 ? (
                    <Champ
                      libelle="Infirmier attitré"
                      id={`infirmier-${i}`}
                      aide="Préférence, pas une affectation figée."
                    >
                      <Liste id={`infirmier-${i}`} {...register(`lignes.${i}.infirmier_prefere_id`)}>
                        <option value="">Indifférent</option>
                        {infirmiers
                          .filter((u) => u.statut === 'ACTIF')
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.prenom} {u.nom}
                            </option>
                          ))}
                      </Liste>
                    </Champ>
                  ) : null}
                </div>

                {/* La phrase produite, relue à chaque frappe. C'est le seul
                    contrôle qu'un infirmier fera vraiment avant d'enregistrer. */}
                {ligne?.type_soin_id ? (
                  <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-800">
                    <Etiquette ton="info">Ce qui sera planifié</Etiquette>{' '}
                    {decrireLigneOrdonnance(
                      {
                        frequence: ligne.frequence,
                        jours_semaine: [...ligne.jours].sort((a, b) => a - b).join(','),
                        intervalle_jours: ligne.intervalle_jours ?? null,
                        horaires: decouperHoraires(ligne.horaires),
                      },
                      soin?.libelle ?? 'Soin',
                      valeurs.date_debut,
                      valeurs.date_fin,
                    )}
                  </p>
                ) : null}
              </fieldset>
            )
          })}
        </section>

        <Bandeau ton="info">
          L&apos;enregistrement ne crée aucune intervention à lui seul : le planning se
          génère depuis les ordonnances, semaine par semaine, depuis l&apos;écran Planning.
        </Bandeau>

        <div className="flex justify-end gap-2">
          <Bouton variante="secondaire" type="button" onClick={() => naviguer(`/patients/${id}`)}>
            Annuler
          </Bouton>
          <Bouton type="submit" disabled={enregistrer.isPending}>
            {enregistrer.isPending ? 'Enregistrement…' : "Enregistrer l'ordonnance"}
          </Bouton>
        </div>
      </form>
    </div>
  )
}
